import { assert } from "@tento-core/assert";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import "@tento-ui/components/calendar/calendar-legend";
import { CalendarLegend } from "@tento-ui/components/calendar/calendar-legend";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import "@tento-ui/components/home-card/row-icon";
import { RowIcon } from "@tento-ui/components/home-card/row-icon";
import { type Option, RowId } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import type { SheetLegendClass } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import "@tento-ui/components/sheet/sheet-legend-layout";
import { SheetLegendLayout } from "@tento-ui/components/sheet/sheet-legend-layout";
import { type SheetRow, type TimeFormat } from "@tento-lona";
import {
  type ModelRow,
} from "@tento-lona/sheets";
import { ColumnTimezoneRendererPlugin } from "../renderer/column-timezone.ts";
import { NaiveDate, NaiveDateTime, NaiveTime, Time, TimeOfDay, TimezoneInfo, TimezoneRegion, type Tzname } from "@tento-chrono";
import { TimezonePreferences } from "@tento-lona";
import { CalendarPreferences } from "@tento-lona";

import { CalendarLegendLayout } from "./timeline-column-legend-layout.ts";
import { LiveData } from "@tento-core/livedata";
import type { CalendarChromeBus } from "./timeline-column/chrome-bus.ts";

import { CalendarAliasRow, CalendarBodyRow } from "@tento-lona/sheets/platform";
import { liveVariantOf } from "../../sheet/live-variant.ts";
@component({
  name: "lona-timeline-column-legend",
})
export class TimelineColumnLegend extends LonaWebComponent {
  #$calendarLayout: SheetLegendLayout = this.hydrate("calendar-layout");
  $calendar: CalendarLegend = this.hydrate("calendar");
  #$calendarIcon: RowIcon = this.hydrate("calendar-icon");
  #$defaultRow: SheetLegend.DefaultRow = this.hydrate("default-row");
  secondaryLegends: Map<
    RowLocalId,
    { $calendar: CalendarLegend; tz: TimezoneRegion }
  > = new Map();
  #primaryTzRegion: Option<TimezoneRegion> = null;
  window: Option<TimeOfDay.Range> = null;
  timeFormat: TimeFormat = "12h";
  row: Option<SheetRow.Joined.Handle> = null;
  // Set by `bind` from the factory's closure-captured deps. Both
  // prefs are app-level singletons; the bus is the body↔legend
  // shared-state channel for cursor + hover state.
  #timezonePreferences: Option<TimezonePreferences> = null;
  #calendarPreferences: Option<CalendarPreferences> = null;
  #chromeBus: Option<CalendarChromeBus> = null;
  #bindGeneration: number = 0;
  /** The rows whose cursor this legend shows, kept per bound row (not walked per pointer move). */
  readonly #cursorRows = new LegendCursorRows();

  bind(
    row: SheetRow.Joined.Handle,
    handle: SheetDelegate,
    deps: TimelineColumnLegend.FactoryDeps,
    icon: HTMLTemplateElement,
  ): Option<Promise<void>> {
    this.#timezonePreferences = deps.tzPrefs;
    this.#calendarPreferences = deps.calPrefs;
    this.#chromeBus = deps.bus;
    this.#bindGeneration++;
    const gen = this.#bindGeneration;
    const rowChanged = this.row?.id !== row.id;
    this.row = row;
    this.#cursorRows.bind(row);
    this.setAttribute("row-id", row.id.toString());

    if (rowChanged) {
      for (const [, entry] of this.secondaryLegends) {
        entry.$calendar.recycle();
      }
      this.secondaryLegends.clear();
    }

    // findAncestor skips self, but the calendar alias's own legend
    // binds against itself — check self before walking up.
    const variant = liveVariantOf(row);
    const cal = variant instanceof CalendarAliasRow
      ? variant
      : row.findAncestor(CalendarAliasRow);
    const effectiveView = cal?.effectiveView(handle.state.sheet.timescale) ?? "daily";
    const calendarType = cal?.calendarType ?? "default";

    const renderCalendar =
      effectiveView !== "monthly" && calendarType !== "list";

    this.style.paddingBottom = renderCalendar
      ? CalendarAliasRow.MARGIN_TOP + CalendarAliasRow.MARGIN_BOTTOM + "px"
      : "0px";

    // Hide both, show active
    this.#$calendarLayout.style.display = "none";
    this.#$defaultRow.style.display = "none";

    if (renderCalendar) {
      this.window = cal?.hoursRange() ?? TimeOfDay.Range.DAY;
      const tzname =
        cal?.timezone ??
        this.#timezonePreferences.effectiveTimezoneName();
      const tz = TimezoneRegion.get(tzname);

      this.timeFormat = this.#calendarPreferences?.peek().timeFormat ?? "12h";
      this.$calendar.use24Hour = this.timeFormat === "24h";

      this.#$calendarLayout.style.display = "block";
      this.#$calendarLayout.bind((target) => handle.rebind.revealRowOptionsPopover(row, target));

      // Set deterministic min-width synchronously so rebindLegend
      // measurement is correct before async tz resolution completes.
      // Walk the rows-side variant tree (not the joined tree) to find
      // timezone descendants. The `alias.secondary-timezones`
      // alias hydrates these from `sheet.preferencesProvider`; they
      // exist only on the rows-side, not as joined-row children.
      const numSecondaries = countRowsSideTzDescendants(row);
      if (numSecondaries === 0) {
        this.#$calendarLayout.style.minWidth = "80px";
      } else {
        // Primary compact (36px) + first secondary non-compact (64px + 12px margin)
        // + additional secondaries compact (36px, no margin)
        const width = 36 + 76 + Math.max(0, numSecondaries - 1) * 36;
        this.#$calendarLayout.style.minWidth = width + "px";
      }

      this.#$calendarIcon.bind(row, { icon });

      // Return the tz chain so callers (sentinel, rebindLegend) can
      // await secondary timezone DOM before measuring width.
      const tzReady = tz.then(async (tr) => {
        if (gen !== this.#bindGeneration) return;
        this.#primaryTzRegion = tr;
        this.bindTzAbbr(TimezoneInfo.tzabbr(tr.today().date.start.tz.info));
        const referenceDate = NaiveDate.today();
        await this.#bindSecondaryTimezones(row, referenceDate);
      });

      this.#attachCurrentCursor();
      return tzReady;
    } else {
      this.#$defaultRow.style.display = "block";
      SheetLegend.DefaultRow.bind(this.#$defaultRow, row, handle, handle.state.sheet.timescale);
      return null;
    }
  }

  bindTzAbbr(tzabbr: string) {
    this.$calendar.bind(tzabbr, this.window ?? TimeOfDay.Range.DAY);
  }

  async #bindSecondaryTimezones(
    row: SheetRow.Joined.Handle,
    referenceDate: NaiveDate,
  ) {
    const primaryTz = this.#primaryTzRegion;
    if (!primaryTz) return;

    const tzChildren = collectRowsSideTzDescendants(row);
    const seenIds = new Set<RowLocalId>();

    for (const child of tzChildren) {
      const secondaryTz = await ColumnTimezoneRendererPlugin.rowTzRegion(child);
      if (!secondaryTz) continue;

      seenIds.add(child.id);

      const offsetMs = tzOffsetMs(
        primaryTz,
        secondaryTz,
        referenceDate,
      );

      const noonInSecondary = secondaryTz.toTz(
        primaryTz.toWallClock(
          new NaiveDateTime(referenceDate, new NaiveTime(12 * Time.MS_PER_HR)),
        ),
      );
      const tzabbr = TimezoneInfo.tzabbr(noonInSecondary.tz.info);
      const window = this.window ?? TimeOfDay.Range.DAY;

      let entry = this.secondaryLegends.get(child.id);
      const reused = entry != null;
      if (!entry) {
        const $cal = CalendarLegend.make();
        $cal.toggleAttribute("secondary", true);
        $cal.use24Hour = this.timeFormat === "24h";
        entry = { $calendar: $cal, tz: secondaryTz };
        this.secondaryLegends.set(child.id, entry);
        this.#$calendarLayout.insertBefore($cal, this.$calendar);
      } else {
        entry.tz = secondaryTz;
        entry.$calendar.use24Hour = this.timeFormat === "24h";
      }
      entry.$calendar.bindOffset(tzabbr, offsetMs, window);
    }

    // Remove legends for children no longer present
    for (const [id, entry] of this.secondaryLegends) {
      if (!seenIds.has(id)) {
        entry.$calendar.recycle();
        this.secondaryLegends.delete(id);
      }
    }

    this.#updateTrailingLines();
  }

  #updateTrailingLines() {
    // All secondaries compact except the leftmost (first)
    let first = true;
    for (const [, entry] of this.secondaryLegends) {
      entry.$calendar.toggleAttribute("compact", !first);
      first = false;
    }
    // Primary is compact when secondaries exist
    this.$calendar.toggleAttribute("compact", this.secondaryLegends.size > 0);
  }

  updateSecondaryTimezones(columns: ColumnIndex.Key.Resolved.Default.Range) {
    const primaryTz = this.#primaryTzRegion;
    if (!primaryTz || this.secondaryLegends.size === 0) return;

    const referenceDate = columns.start.discrete()[0].start;
    const window = this.window ?? TimeOfDay.Range.DAY;

    for (const [, entry] of this.secondaryLegends) {
      const offsetMs = tzOffsetMs(
        primaryTz,
        entry.tz,
        referenceDate,
      );
      const noonInSecondary = entry.tz.toTz(
        primaryTz.toWallClock(
          new NaiveDateTime(referenceDate, new NaiveTime(12 * Time.MS_PER_HR)),
        ),
      );
      const tzabbr = TimezoneInfo.tzabbr(noonInSecondary.tz.info);
      entry.$calendar.bindOffset(tzabbr, offsetMs, window);
    }
  }

  #attachCurrentCursor() {
    this.#chromeBus?.currentCursor.addListener(
      this,
      (props) => {
        if (!this.row || !props || !this.showsCursorOf(props.rid)) {
          this.$calendar.bindCursor(null);
          for (const [, entry] of this.secondaryLegends) {
            entry.$calendar.bindCursor(null);
          }
        } else {
          const clamped = TimeOfDay.clamp(props.offsetMs);
          this.$calendar.bindCursor(clamped);
          for (const [, entry] of this.secondaryLegends) {
            entry.$calendar.bindCursor(clamped);
          }
        }
      },
      LiveData.TriggerImmediately,
    );
  }

  /**
   * Whether a cursor (or hover) reported against `rowId` is this legend's:
   * its row's or its calendar body's (`LegendCursorRows`: computed once per
   * bound row and again after the sheet's tree is rebuilt, so a body
   * re-created at any depth under the bound row still matches without a
   * subtree walk per move).
   */
  showsCursorOf(rowId: RowLocalId): boolean {
    return this.row != null && this.#cursorRows.has(rowId);
  }

  toggleCurrentCursorDisplay(shown: boolean) {
    this.$calendar.bindCursorDisplay(shown);
    for (const [, entry] of this.secondaryLegends) {
      entry.$calendar.bindCursorDisplay(shown);
    }
  }

  connectedCallback(): void {
    this.#attachCurrentCursor();
    this.#chromeBus?.attach(this);
    this.#chromeBus?.queuePointerEnterLeave();
  }

  disconnectedCallback(): void {
    this.#chromeBus?.currentCursor.removeListener(this);
    this.#chromeBus?.detach(this);
    for (const [, entry] of this.secondaryLegends) {
      entry.$calendar.recycle();
    }
    this.secondaryLegends.clear();
  }

  onRecycle() {
    for (const [, entry] of this.secondaryLegends) {
      entry.$calendar.recycle();
    }
    this.secondaryLegends.clear();
  }

  /**
   * Factory closure capturing app-level prefs (`TimezonePreferences`,
   * `CalendarPreferences`) and the plugin's default icon. Prefs are
   * `LonaSdk.Client`-scoped singletons; DI'ing them through the factory
   * removes the legacy "read prefs off the plugin instance" pattern.
   */
  static factory(
    deps: TimelineColumnLegend.FactoryDeps,
    icon: HTMLTemplateElement,
  ): SheetLegendClass<TimelineColumnLegend> {
    return class {
      static make(): TimelineColumnLegend {
        return TimelineColumnLegend.make();
      }

      static bind(
        $legend: TimelineColumnLegend,
        row: SheetRow.Joined.Handle,
        handle: SheetDelegate,
      ): Promise<void> | void {
        return $legend.bind(row, handle, deps, icon) ?? undefined;
      }

      static computeWidth(row: SheetRow.Joined.Handle): number {
        const tzCount = countRowsSideTzDescendants(row);
        return CalendarLegendLayout.width({ childTimezoneCount: tzCount });
      }
    };
  }

  static $html: Option<HTMLTemplateElement> = html`
    <std-sheet-legend-layout id="calendar-layout" style="display: none">
      <std-calendar-legend id="calendar"></std-calendar-legend>
      <std-row-icon id="calendar-icon" slot="icon" emoji-target></std-row-icon>
    </std-sheet-legend-layout>
    <std-sheet-legend-default-row id="default-row" style="display: none">
    </std-sheet-legend-default-row>
  `;

  static $styles: Style.Compat[] = [css`
    #calendar-layout {
      padding-top: 12px;
    }
    #calendar-icon {
      --p-hover-color: transparent;
    }
  `];
}

export namespace TimelineColumnLegend {
  export type FactoryDeps = {
    tzPrefs: TimezonePreferences;
    calPrefs: CalendarPreferences;
    bus: CalendarChromeBus;
  };
}

/**
 * The rows whose time cursor a calendar legend shows: its own row, and the
 * `calendar-body` layout rows of its calendar. The body's gestures report the
 * cursor and hover against the row its day columns bind (`GestureInfo.rowId`,
 * the day-column renderer's anchor row), and for a calendar that row is its
 * `calendar-body` child, not the alias row the legend binds. Matching only the
 * legend's own row left the cursor label uncreated and the hover marker off
 * (browser spec calendar/legend-cursor-label). Nested calendars keep
 * their own legend, so the walk stops at them.
 */
export function legendCursorRowIds(row: SheetRow.Joined.Handle): ReadonlySet<RowLocalId> {
  const ids = new Set<RowLocalId>([row.id]);
  const stack: SheetRow.Joined.Handle[] = [...row.children()];
  for (let r = stack.pop(); r != null; r = stack.pop()) {
    const variant = liveVariantOf(r);
    if (variant instanceof CalendarAliasRow) continue;
    if (variant instanceof CalendarBodyRow) ids.add(r.id);
    stack.push(...r.children());
  }
  return ids;
}

/**
 * `legendCursorRowIds` of one bound row, kept: the walk runs once per bound
 * row and tree, and again when the sheet's joined tree was rebuilt since (a
 * structural change at any depth: a body re-created two levels down is one).
 * A pointer move costs an identity check and a lookup, not a subtree walk
 * and not an allocation.
 */
export class LegendCursorRows {
  readonly #walk: (row: SheetRow.Joined.Handle) => ReadonlySet<RowLocalId>;
  readonly #structureOf: (row: SheetRow.Joined.Handle) => unknown;
  #row: Option<SheetRow.Joined.Handle> = null;
  #structure: unknown = null;
  #ids: ReadonlySet<RowLocalId> = new Set();

  /** `walk` and `structure` default to the module's own (a test counts the walks, or moves the structure key). */
  constructor(
    walk: (row: SheetRow.Joined.Handle) => ReadonlySet<RowLocalId> = legendCursorRowIds,
    structure: (row: SheetRow.Joined.Handle) => unknown = legendStructureKey,
  ) {
    this.#walk = walk;
    this.#structureOf = structure;
  }

  /** The legend's row: a new handle recomputes (a rebind is a structure change at most). */
  bind(row: SheetRow.Joined.Handle): void {
    if (this.#row === row) return;
    this.#row = row;
    this.#refresh(row);
  }

  /** Whether `rowId` is the bound row's or one of its calendar bodies' in the current tree. */
  has(rowId: RowLocalId): boolean {
    const row = this.#row;
    if (row == null) return false;
    if (this.#structureOf(row) !== this.#structure) this.#refresh(row);
    return this.#ids.has(rowId);
  }

  #refresh(row: SheetRow.Joined.Handle): void {
    this.#structure = this.#structureOf(row);
    this.#ids = this.#walk(row);
  }
}

/**
 * The bound row's place in the sheet's joined tree. A row's facade is the
 * same object across tree rebuilds, but every rebuild (any structural change
 * of the sheet) places it in a new `SheetRow.Joined`, and its children, at any
 * depth, are read through that placement only: a new placement is the one
 * sign that the walk's answer may have changed.
 */
export function legendStructureKey(row: SheetRow.Joined.Handle): SheetRow.Joined {
  return row.joined;
}

// ── Rows-side tz descendant walker ──
//
// The `alias.secondary-timezones` alias hydrates tz children
// only on the rows-side (`sheet.preferencesProvider`). The lona-js
// joined tree doesn't carry them, so the legend walker traverses
// `row.variant.children` instead of `row.children()`. Stops descending
// at nested calendars, mirroring the previous joined-tree walker.
function collectRowsSideTzDescendants(
  row: SheetRow.Joined.Handle,
): ModelRow[] {
  const variant = liveVariantOf(row);
  if (variant == null) return [];
  const out: ModelRow[] = [];
  const walk = (rows: readonly ModelRow[]) => {
    for (const r of rows) {
      const wireType = wireTypeOfRowsSide(r);
      if (wireType === "timezone" || wireType === "alias.timezone") out.push(r);
      if (wireType === "calendar" || wireType === "alias.calendar") continue;
      walk(r.children);
    }
  };
  walk(variant.children);
  return out;
}

function countRowsSideTzDescendants(row: SheetRow.Joined.Handle): number {
  return collectRowsSideTzDescendants(row).length;
}

/**
 * Best-effort wire-type read off a rows-side `SheetRow`. Alias rows
 * carry `descriptor.template`, layouts/sources have other shapes.
 * We only need to disambiguate `"timezone"` and `"calendar"` for the
 * walker.
 */
function wireTypeOfRowsSide(row: ModelRow): string {
  const d = row.descriptor;
  if (d.role === "alias") return d.template;
  return "";
}

/** The wall-clock offset of `secondaryTz` from `primaryTz` at noon of `referenceDate`, in ms. */
function tzOffsetMs(
  primaryTz: TimezoneRegion,
  secondaryTz: TimezoneRegion,
  referenceDate: NaiveDate,
): number {
  const noonNdt = new NaiveDateTime(referenceDate, new NaiveTime(12 * Time.MS_PER_HR));
  const primaryDt = primaryTz.toWallClock(noonNdt);
  // Convert to UTC before passing to toTz — TimezoneRegion.toTz uses
  // dt.asUtc() internally which only works on UTC DateTimes.
  const secondaryDt = secondaryTz.toTz(primaryDt.toUtc());

  const primaryWallMs =
    noonNdt.time.toMs + noonNdt.date.dse * Time.MS_PER_DAY;
  const secondaryWallMs =
    secondaryDt.ndt.time.toMs + secondaryDt.ndt.date.dse * Time.MS_PER_DAY;

  return secondaryWallMs - primaryWallMs;
}
